import { useEffect, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Play, Camera } from 'lucide-react';
import { PAST_EDITIONS, getPastEditionBySlug, getFullImageUrl } from '../../data/pastEditions';
import pastContent from '../../data/pastEditionsContent.json';

const contentMap = pastContent.reduce((acc, c) => { acc[c.slug] = c; return acc; }, {});

export const PastEditionDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const edition = getPastEditionBySlug(slug);
  const content = contentMap[slug];

  useEffect(() => {
    if (!edition) {
      document.title = 'Edizione non trovata — SIOS';
      return;
    }
    document.title = `SIOS ${edition.name} ${edition.year} — Edizioni Precedenti`;
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [edition]);

  const sameYear = useMemo(
    () => edition ? PAST_EDITIONS.filter(e => e.year === edition.year && e.slug !== edition.slug) : [],
    [edition]
  );

  if (!edition) {
    return (
      <div className="min-h-screen bg-sios-void text-white">
        <div className="max-w-3xl mx-auto px-4 py-20 text-center" data-testid="past-edition-not-found">
          <h1 className="font-heading text-4xl font-bold mb-4">Edizione non trovata</h1>
          <p className="text-muted-foreground mb-6">L&apos;edizione richiesta non esiste o è stata rimossa.</p>
          <button
            onClick={() => navigate('/edizioni-precedenti')}
            className="inline-flex items-center gap-2 text-primary hover:underline"
          >
            <ArrowLeft size={14} /> Torna alla lista
          </button>
        </div>
      </div>
    );
  }

  const paragraphs = content?.paragraphs || [];
  const gallery = content?.gallery || [];
  const youtubeIds = content?.youtube_ids || [];
  const speakers = content?.speakers || [];

  return (
    <div className="min-h-screen bg-sios-void text-white">
      {/* Hero */}
      <div className="relative w-full aspect-[16/7] sm:aspect-[16/6] overflow-hidden bg-black">
        <img
          src={getFullImageUrl(edition.image)}
          alt={`SIOS ${edition.name} ${edition.year}`}
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="past-edition-hero"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-sios-void via-sios-void/60 to-sios-void/20" />
        <div className="absolute inset-x-0 bottom-0 p-6 sm:p-10">
          <Link to="/edizioni-precedenti" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-white mb-4" data-testid="back-list-link">
            <ArrowLeft size={14} /> Tutte le edizioni
          </Link>
          <p className="inline-flex items-center gap-2 text-primary text-sm font-medium tracking-wider uppercase">
            <Calendar size={14} /> SIOS {edition.year}
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold mt-2" data-testid="past-edition-title">
            {edition.name} <span className="text-primary">{edition.year}</span>
          </h1>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-10 sm:py-16 space-y-16">
        {/* Description paragraphs */}
        {paragraphs.length > 0 && (
          <section className="prose prose-invert max-w-3xl" data-testid="past-edition-paragraphs">
            {paragraphs.map((p, i) => {
              if (p.tag === 'h2') return <h2 key={i} className="font-heading text-3xl font-bold mt-8 mb-3">{p.text}</h2>;
              if (p.tag === 'h3') return <h3 key={i} className="font-heading text-2xl font-bold mt-6 mb-2">{p.text}</h3>;
              if (p.tag === 'li') return <p key={i} className="text-muted-foreground leading-relaxed flex gap-2"><span className="text-primary">•</span> <span>{p.text}</span></p>;
              return <p key={i} className="text-muted-foreground leading-relaxed mb-4">{p.text}</p>;
            })}
          </section>
        )}

        {/* YouTube videos */}
        {youtubeIds.length > 0 && (
          <section data-testid="past-edition-videos">
            <h2 className="font-heading text-2xl font-bold mb-6 flex items-center gap-2">
              <Play size={20} className="text-primary" /> Video dell&apos;edizione
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {youtubeIds.map((id) => (
                <div key={id} className="aspect-video rounded-xl overflow-hidden bg-black/40">
                  <iframe
                    src={`https://www.youtube.com/embed/${id}`}
                    title={`Video ${id}`}
                    className="w-full h-full"
                    loading="lazy"
                    allowFullScreen
                    allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  />
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Speakers (if extracted) */}
        {speakers.length > 0 && (
          <section data-testid="past-edition-speakers">
            <h2 className="font-heading text-2xl font-bold mb-6">Speaker</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-5">
              {speakers.filter(s => s.image).map((s, i) => (
                <div key={`${s.name}-${i}`} className="text-center">
                  <img
                    src={getFullImageUrl(s.image)}
                    alt={s.name}
                    loading="lazy"
                    className="w-full aspect-square rounded-xl object-cover ring-2 ring-white/10"
                  />
                  <p className="font-heading font-bold mt-3 leading-tight">{s.name}</p>
                  {s.role && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{s.role}</p>}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Gallery */}
        {gallery.length > 0 && (
          <section data-testid="past-edition-gallery">
            <h2 className="font-heading text-2xl font-bold mb-6 flex items-center gap-2">
              <Camera size={20} className="text-primary" /> Gallery
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {gallery.map((g, i) => (
                <a
                  key={i}
                  href={getFullImageUrl(g.image)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block aspect-square rounded-lg overflow-hidden bg-black/40 hover:opacity-80 transition-opacity"
                >
                  <img
                    src={getFullImageUrl(g.image)}
                    alt={g.alt || `Foto ${i + 1}`}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                </a>
              ))}
            </div>
          </section>
        )}

        {/* Same year related */}
        {sameYear.length > 0 && (
          <section data-testid="same-year-section">
            <h3 className="font-heading text-xl font-bold mb-6">Altre edizioni del {edition.year}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {sameYear.map((ed) => (
                <Link
                  key={ed.slug}
                  to={`/edizioni-precedenti/${ed.slug}`}
                  className="group block rounded-xl overflow-hidden border border-white/10 bg-white/5 hover:border-primary/50 transition-all"
                  data-testid={`related-${ed.slug}`}
                >
                  <div className="aspect-[750/465] overflow-hidden bg-black/40">
                    <img
                      src={getFullImageUrl(ed.image)}
                      alt={`${ed.name} ${ed.year}`}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <h4 className="font-heading text-lg font-bold leading-tight group-hover:text-primary transition-colors">{ed.name}</h4>
                    <p className="text-muted-foreground text-sm">{ed.year}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};
